import React, { Component } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { ToastManagerProp, withToastManager } from 'react-toast-notifications';
import { CSSTransition } from 'react-transition-group';

import CourseConfirm from 'components/Course/CourseConfirm';
import CourseImport from 'components/Course/CourseImport';
import CourseInfo from 'components/Course/CourseInfo';
import CourseSchedule from 'components/Course/CourseSchedule';
import CourseTimeline from 'components/Course/CourseTimeline';
import { Loader } from 'components/UtilElements';
import { apiErrorHandler } from 'core/errors';
import { Dispatch } from 'redux';
import { COURSE_CREATED, UPDATE_COURSE_DETAILS } from 'redux/actions';
import { API, getCancelToken, request } from 'redux/api';
import { RootState } from 'redux/reducers';

interface GetCourseDetailsResponse extends CourseDetails, MessageResponse {}

interface CourseState {
  screen: CourseScreen;
  isSavingSchedule: boolean;
}

interface RouteState {
  courseId: string;
}

interface CourseProps extends ToastManagerProp, RouteComponentProps<never, never, RouteState> {
  course: CourseDetails | null;
  getAccess: SessionData | null;
  updateCourse(course: CourseDetails): Promise<void>;
  createCourse(courseId: string): Promise<void>;
}

class Course extends Component<CourseProps, CourseState> {
  apiSources = getCancelToken();

  state: CourseState = {
    screen: 'loading',
    isSavingSchedule: false,
  };

  setScreen = (status: CourseStatus) => {
    let step: CourseScreen = 'course-info';

    if (status.infoSet === 0) {
      step = 'course-info';
    } else if (status.scheduleSet === 0) {
      step = 'course-schedule';
    } else if (status.studentsEnrolled === 0) {
      step = 'course-import';
    } else {
      step = 'course-confirm';
    }

    this.setState({ screen: step });
  };

  getCourseDetails(id: string) {
    return request<GetCourseDetailsResponse>({
      url: API.GET_COURSE_DETAILS(id),
      cancelToken: this.apiSources.token,
    });
  }

  private initializeCourse(course: CourseDetails) {
    const { isAdminSet, instructors } = course.team;
    const { courseTimezone } = course.dates;

    /**
     * if there is only one instructor and admin is not set
     * then make that single instructor as admin by default
     */
    if (!isAdminSet && instructors.length === 1 && instructors[0].valid) {
      course.team.isAdminSet = 1;
      course.team.instructors = [];
      course.team.admin = instructors[0];
    }

    /**
     * if there is only one course timezone available
     * and timezone is not set then select it by default
     */
    if (!courseTimezone.valid && courseTimezone.available.length === 1) {
      courseTimezone.valid = 1;
      courseTimezone.value = courseTimezone.available[0];
    }

    this.props.updateCourse(course);
    this.setScreen(course.status);
  }

  async componentDidMount() {
    const sessionData = this.props.getAccess;
    const context = sessionData?.context;

    this.apiSources = getCancelToken();

    try {
      const courseId =
        context === 'course' ? sessionData?.courseDetails.courseId : this.props.location.state.courseId;

      if (courseId) {
        const { message: _message, ...course } = await this.getCourseDetails(courseId);
        this.initializeCourse(course);
      } else {
        throw new Error('courseId is not provided!');
      }
    } catch (err) {
      apiErrorHandler(this.props.toastManager, err);
    }
  }

  componentWillUnmount() {
    this.apiSources.cancel();
  }

  onNext = () => {
    const { screen } = this.state;

    let nextScreen: CourseScreen | undefined;
    const { course } = this.props;

    if (!course) {
      console.warn('course not found in AppState');
      return;
    }

    switch (screen) {
      case 'course-info':
        this.setState({ screen: 'course-schedule' });
        break;
      case 'course-schedule':
        this.setState({ screen: 'course-timeline' });
        break;
      case 'course-timeline':
        this.setState({ screen: 'course-import' });
        break;
      case 'course-import':
        this.setState({ screen: 'course-confirm' });
        break;
      default:
        nextScreen = screen;
        break;
    }

    if (!nextScreen) return;
    this.setState({ screen: nextScreen });
  };

  getScreen = () => {
    const { screen } = this.state;

    switch (screen) {
      case 'loading':
        return <Loader />;
      case 'course-info':
        return <CourseInfo onNext={this.onNext} />;
      case 'course-schedule':
        return <CourseSchedule onNext={this.onNext} />;
      case 'course-timeline':
        return <CourseTimeline onNext={this.onNext} />;
      case 'course-import':
        return <CourseImport onNext={this.onNext} />;
      case 'course-confirm':
        return <CourseConfirm />;
      default:
        return null;
    }
  };

  render() {
    return (
      <div className='import-course'>
        <h1 className='import-course__header'>Importing course into Acadly</h1>
        <CSSTransition classNames='fade' timeout={300}>
          {this.getScreen()}
        </CSSTransition>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  course: state.courseInCreation,
  getAccess: state.getAccess,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateCourse: async (course: CourseDetails) => {
    dispatch({
      type: UPDATE_COURSE_DETAILS,
      payload: course,
    });
  },
  createCourse: async (courseId: string) => {
    dispatch({
      type: COURSE_CREATED,
      payload: courseId,
    });
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withToastManager(Course)));
